import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { startCustomCheckout, SERVICE_MENU } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { avisoPagosPronto, pagosNoDisponibles } from "@/lib/pagos";

export default function CustomPlanCard({ selected, onToggle, onContact }) {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const chosen = SERVICE_MENU.filter((s) => selected.includes(s.id));
  const total = chosen.reduce((a, s) => a + s.price, 0);

  const proceed = async () => {
    if (selected.length === 0) {
      toast.info(t.custom.toasts.empty);
      return;
    }
    setLoading(true);
    try {
      const { data } = await startCustomCheckout(selected);
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.error(t.custom.toasts.error);
    } catch (e) {
      if (pagosNoDisponibles(e)) avisoPagosPronto(t, onContact);
      else toast.error(t.custom.toasts.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      {...{
        initial: { opacity: 0, y: 36 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.15 },
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.32 },
      }}
      data-testid="pricing-col-custom"
      className="relative bg-white rounded-xl p-8 flex flex-col border border-[#E2E8F0] hover:shadow-[0_12px_24px_rgba(15,30,61,0.08)] transition-all duration-300 hover:-translate-y-1"
    >
      <div className="eyebrow text-[#C5B358]">04 / {t.pricing.plans.enterprise.kicker === "Mandato de Gobernanza" ? "Servicios Individuales" : "Individual Services"}</div>
      <h3 className="mt-3 text-lg font-bold tracking-[0.04em] uppercase text-[#0F1E3D]">ATLANTIS TAILORED</h3>

      <div className="mt-5 flex items-baseline gap-2" data-testid="custom-calc-total-display">
        <motion.span
          key={total}
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="font-display text-[28px] font-bold text-[#0F1E3D] tabular-nums"
        >
          ${total.toLocaleString("en-US")}
        </motion.span>
        <span className="text-base text-[#64748B]">USD</span>
      </div>
      <div className="text-[13px] text-[#64748B] mt-1.5">{t.custom.subtitle}</div>

      <p className="text-[13px] font-medium text-[#1E293B] border-t border-[#E2E8F0] mt-4 pt-4">
        {t.custom.select}
      </p>

      <div className="my-3 flex-grow">
        {SERVICE_MENU.map((s) => {
          const on = selected.includes(s.id);
          return (
            <button
              key={s.id}
              role="checkbox"
              aria-checked={on}
              data-testid={s.testid}
              onClick={() => onToggle(s.id)}
              className="group flex items-center justify-between w-full py-3 border-b border-dashed border-[#E2E8F0] text-left transition-colors hover:bg-[#F8FAFC] -mx-2 px-2 rounded"
            >
              <span className="flex items-center gap-2.5 min-w-0">
                <span
                  className={`w-[16px] h-[16px] rounded-[4px] border flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                    on ? "bg-[#C5B358] border-[#C5B358]" : "border-[#CBD5E1] bg-white group-hover:border-[#C5B358]"
                  }`}
                >
                  {on && <Check size={10} strokeWidth={3.5} className="text-[#0F1E3D]" />}
                </span>
                <span className={`text-sm leading-snug ${on ? "text-[#0F1E3D] font-semibold" : "text-[#1E293B]"}`}>
                  {s.name}
                </span>
              </span>
              <span
                className={`text-sm tabular-nums flex-shrink-0 ml-3 ${
                  on ? "text-[#C5B358] font-bold" : "text-[#64748B] font-medium"
                }`}
              >
                +${s.price.toLocaleString("en-US")}
              </span>
            </button>
          );
        })}
      </div>

      <div className="text-xs text-[#64748B] mt-3 mb-4" data-testid="custom-calc-state">
        {selected.length === 0
          ? t.custom.empty
          : t.custom.state(selected.length, total)}
      </div>

      <button
        data-testid="btn-custom-plan"
        onClick={proceed}
        disabled={loading}
        className="mt-auto w-full rounded-md py-3.5 bg-[#0F1E3D] hover:bg-[#1a2e5c] text-white text-[12px] font-bold uppercase tracking-[0.1em] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {loading && <Loader2 size={14} className="animate-spin" />}
        PROCEED WITH CUSTOM PLAN
      </button>
    </motion.div>
  );
}